import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/** Forced password change screen. Shown immediately after login when the
 *  user record carries `must_change_password=true` (e.g. the seeded
 *  admin@aivideoos.com on first login). Until cleared, every other route
 *  redirects here.
 */
export default function ChangePassword() {
    const { user, refreshUser, logout } = useAuth();
    const nav = useNavigate();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);

    if (!user) return <Navigate to="/login" replace />;
    if (!user.must_change_password) return <Navigate to="/dashboard" replace />;

    const submit = async (e) => {
        e?.preventDefault?.();
        if (next.length < 8) { toast.error("Password must be 8+ characters."); return; }
        if (next !== confirm) { toast.error("Passwords don't match."); return; }
        setBusy(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Password updated.");
            await refreshUser?.();
            nav("/dashboard");
        } catch (err) {
            toast.error(err?.userMessage || "Could not update password.");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6" data-testid="change-password-page">
            <form onSubmit={submit} className="w-full max-w-md card-base p-6 sm:p-8">
                <div className="flex items-start gap-3 mb-6">
                    <div className="w-10 h-10 rounded-sm border border-[#FF4400]/40 bg-[#FF4400]/10 flex items-center justify-center shrink-0">
                        <Lock className="w-5 h-5 text-[#FF4400]" />
                    </div>
                    <div>
                        <div className="label-micro text-[#FF4400]">First-login security</div>
                        <h1 className="font-display text-2xl mt-0.5">Set a new password</h1>
                        <p className="text-xs text-zinc-500 mt-2">
                            For your protection, the bootstrap admin credential must be changed before continuing.
                        </p>
                    </div>
                </div>
                <div className="space-y-4">
                    <Field label="Current password">
                        <input
                            type="password"
                            data-testid="cp-current"
                            value={current}
                            onChange={e => setCurrent(e.target.value)}
                            className="form-input" autoFocus
                            placeholder="••••••"
                        />
                    </Field>
                    <Field label="New password (8+ chars)">
                        <input
                            type="password"
                            data-testid="cp-new"
                            value={next}
                            onChange={e => setNext(e.target.value)}
                            className="form-input"
                            placeholder="Strong password"
                        />
                    </Field>
                    <Field label="Confirm new password">
                        <input
                            type="password"
                            data-testid="cp-confirm"
                            value={confirm}
                            onChange={e => setConfirm(e.target.value)}
                            className="form-input"
                            placeholder="Re-enter"
                        />
                    </Field>
                </div>
                <button
                    type="submit"
                    disabled={busy}
                    data-testid="cp-submit"
                    className="btn-accent text-sm w-full mt-6"
                >
                    <ShieldCheck className="w-4 h-4" />
                    {busy ? "Saving…" : "Update password"}
                </button>
                <button
                    type="button"
                    onClick={logout}
                    className="text-xs text-zinc-500 hover:text-white block mx-auto mt-4"
                >
                    Sign out instead
                </button>
            </form>
            <Toaster theme="dark" />
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="label-micro block mb-2">{label}</label>
            {children}
        </div>
    );
}
